<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useAbility } from "@casl/vue";
import type { IAdminPageProps } from "@/utils/adminInterfaces";
import { useAuthStore } from "@/stores/authStore";
import { EEnvironmentType, useEnvironmentStore } from "@/stores/environmentStore";
import { tourDialogVisible } from "@/composables/shared/useProductTour";
import PresentationRoleShelf from "@/components/shared/PresentationRoleShelf.vue";
import NavbarActions from "./NavbarActions.vue";
import NavbarDesktopNavigation from "./NavbarDesktopNavigation.vue";
import NavbarMobileDrawer from "./NavbarMobileDrawer.vue";
import { useNavbarItems } from "./useNavbarItems";
import { useNavbarLayout } from "./useNavbarLayout";
import Logo from "@/components/shared/Logo.vue";
import { useNavbarSubItems } from "./useNavbarSubItems";
import SubNavbar, { SUBNAVBAR_HEIGHTS_PX } from "@/components/shared/navbar/SubNavbar.vue";

const props = defineProps<
  {
    fullscreen?: boolean;
    isAdminPage?: boolean;
  } & Partial<IAdminPageProps>
>();

const { t } = useI18n();
const { can } = useAbility();
const authStore = useAuthStore();
const environmentStore = useEnvironmentStore();
const { loggedAsStaff } = storeToRefs(authStore);
const navMobileDrawer = ref(false);

const navBarShellRef = useTemplateRef<HTMLElement>("navBarShell");
const appBarRef = useTemplateRef<{ $el?: HTMLElement }>("appBar");

const openTutorialDialog = () => {
  navMobileDrawer.value = false;
  tourDialogVisible.value = true;
};

const { navItems, menuItems, helpItems } = useNavbarItems(openTutorialDialog);

const {
  mobile,
  mdAndDown,
  mdAndUp,
  lgAndUp,
  compacted,
  itemSize,
  mobileActionBtnColor,
  drawerLayerStyle,
  appBarHeightProp,
} = useNavbarLayout({
  fullscreen: () => props.fullscreen,
  drawerOpen: navMobileDrawer,
  navBarShellRef,
  appBarRef,
});

const educationTitleSuffix = computed(() => {
  const education = authStore.authUser?.logged_staff?.education?.[0];
  if (!education) return "";

  return `, ${t(`generic.navbar.educationTitles.${education.toLowerCase()}`)}`;
});

const showDesktopNav = computed(() => lgAndUp.value && !props.isAdminPage);

const navBarShellClass = computed(() =>
  navMobileDrawer.value && mdAndDown.value ? "tw:z-nav-drawer-open-navbar" : "tw:z-sticky-header"
);

const { subNavItems, sectionsByRootUrl } = useNavbarSubItems();

const showSubNavbar = computed(() => subNavItems.value.length > 0 && lgAndUp.value);

const subNavbarHeight = SUBNAVBAR_HEIGHTS_PX.full;
</script>

<template>
  <PresentationRoleShelf
    v-if="
      can($permActions.SHOW_LOGINHINTBADGE, $permSubjects.USER__LOGINHINTBADGE) &&
      environmentStore.currentEnv !== EEnvironmentType.PRODUCTION &&
      !mobile
    "
    class="tw:absolute tw:inset-x-0 tw:top-0"
    :compact="compacted"
  />

  <div
    ref="navBarShell"
    :class="[
      'tw:fixed tw:inset-x-0 tw:top-0 tw:bg-surface/95 tw:backdrop-blur-md',
      navBarShellClass,
    ]"
  >
    <v-app-bar
      ref="appBar"
      class="navbar-app-bar tw:!transition-all tw:duration-500 tw:ease-in-out"
      :color="mdAndDown && compacted ? 'background' : 'surface'"
      data-testid="navbar"
      :density="compacted ? 'compact' : 'default'"
      :elevation="0"
      :extended="showSubNavbar"
      :extension-height="showSubNavbar ? subNavbarHeight : 0"
      :height="appBarHeightProp"
    >
      <v-container
        :class="[
          'tw:h-full tw:py-0! tw:px-4! tw:md:px-6!',
          {
            'tw:max-w-[1366px]!': !fullscreen,
            'tw:max-w-full!': isAdminPage || fullscreen,
          },
        ]"
      >
        <div class="tw:flex tw:h-full tw:items-center">
          <Logo :compacted="compacted" />

          <NavbarDesktopNavigation
            v-if="showDesktopNav"
            :items="navItems"
            :compacted="compacted"
          />

          <div class="tw:!ml-auto tw:flex tw:items-center">
            <div
              v-if="showDesktopNav"
              class="tw:!ml-4 tw:whitespace-nowrap tw:text-right tw:text-[12.5px] tw:font-[650] tw:leading-[1.2] tw:tracking-[-0.005em] tw:text-primary"
            >
              {{
                t("generic.navbar.greetings", {
                  name: loggedAsStaff?.first_name,
                  title: educationTitleSuffix,
                })
              }}
            </div>

            <div
              v-if="showDesktopNav || (fullscreen && mdAndUp)"
              class="tw:mx-4 tw:h-[26px] tw:w-px tw:flex-none tw:self-center tw:bg-on-surface/10"
            />

            <NavbarActions
              :compacted="compacted"
              :drawer-open="navMobileDrawer"
              :is-admin-page="isAdminPage"
              :item-size="itemSize"
              :lg-and-up="lgAndUp"
              :md-and-down="mdAndDown"
              :mobile-action-btn-color="mobileActionBtnColor"
              @close-drawer="navMobileDrawer = false"
              @open-tutorial="openTutorialDialog"
              @toggle-drawer="navMobileDrawer = !navMobileDrawer"
            />
          </div>
        </div>
      </v-container>
      <template #extension>
        <SubNavbar
          :fullscreen="fullscreen"
          :items="subNavItems"
          :compact="compacted"
        />
      </template>
    </v-app-bar>

    <NavbarMobileDrawer
      v-if="mdAndDown"
      v-model="navMobileDrawer"
      :categories="categories"
      :compacted="compacted"
      :drawer-layer-style="drawerLayerStyle"
      :handle-direct-select="handleDirectSelect"
      :help-items="helpItems"
      :is-admin-page="isAdminPage"
      :item-size="itemSize"
      :menu-items="menuItems"
      :nav-items="navItems"
      :sub-nav-sections="sectionsByRootUrl"
    />
  </div>
</template>

<style scoped lang="scss">
.navbar-app-bar {
  :deep(.v-toolbar__content) {
    position: relative;
    z-index: 1;
    overflow: visible;
    box-shadow: 0 1px 3px rgba(14, 43, 58, 0.08);
    padding-top: env(safe-area-inset-top);
  }
}
</style>
